import * as XLSX from "xlsx";

/**
 * Download an array of row objects as an .xlsx workbook (Excel-native).
 * columns: [{ key, label, format? }] — `format` is an optional (value, row) => string
 */
export function downloadRowsAsXlsx({ rows, columns, filename, sheetName = "Sheet1" }) {
  const header = columns.map((c) => c.label);
  const data = rows.map((row) => {
    const out = {};
    columns.forEach((c) => {
      const raw = row[c.key];
      out[c.label] = c.format ? c.format(raw, row) : raw;
    });
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(data, { header });
  // Reasonable column widths based on header length
  ws["!cols"] = columns.map((c) => ({ wch: Math.max(12, (c.label || "").length + 4) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const stamp = new Date().toISOString().slice(0, 10);
  const name = filename.endsWith(".xlsx") ? filename : `${filename}-${stamp}.xlsx`;
  XLSX.writeFile(wb, name);
}

/**
 * Open a print-optimised window with the given HTML table content and trigger print.
 * `title` is used for both the tab title and the page heading.
 * `bodyHtml` should be a <table>…</table> string; the wrapper adds brand-neutral CSS.
 */
export function openPrintWindow({ title, subtitle, bodyHtml, footerHtml = "" }) {
  const w = window.open("", "_blank", "width=1100,height=780");
  if (!w) return false;
  const now = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  w.document.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title>
<meta charset="utf-8" />
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: 'Inter', system-ui, sans-serif; color: #1c211d; margin: 24px; }
  h1 { font-family: 'Playfair Display', 'Times New Roman', serif; font-weight: 600; margin: 0 0 4px; color: #2F5D50; font-size: 22px; }
  .sub { color: #4E534D; font-size: 12px; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  thead th { text-align: left; background: #2F5D50; color: #FFFDF7; padding: 8px; font-weight: 500; letter-spacing: 0.04em; }
  tbody td { padding: 8px; border-bottom: 1px solid #E5E5DE; }
  tbody tr:nth-child(even) td { background: #FAF8F1; }
  .num { text-align: right; font-family: 'IBM Plex Mono', ui-monospace, monospace; }
  .foot { color: #4E534D; font-size: 10px; margin-top: 14px; letter-spacing: 0.06em; text-transform: uppercase; text-align: right; }
  @page { margin: 12mm; }
  @media print {
    button { display: none; }
  }
</style></head><body>
<h1>${escapeHtml(title)}</h1>
<div class="sub">${escapeHtml(subtitle || "")}</div>
${bodyHtml}
<div class="foot">Generated ${escapeHtml(now)} · King's PMS ${footerHtml ? "· " + footerHtml : ""}</div>
<script>window.addEventListener('load', () => { setTimeout(() => { window.focus(); window.print(); }, 300); });</script>
</body></html>`);
  w.document.close();
  return true;
}

/** Build an HTML <table> string for print from rows + columns. */
export function rowsToHtmlTable({ rows, columns }) {
  const thead = `<thead><tr>${columns.map((c) => `<th${c.numeric ? ' class="num"' : ""}>${escapeHtml(c.label)}</th>`).join("")}</tr></thead>`;
  const tbody = `<tbody>${rows.map((row) => `<tr>${columns.map((c) => {
    const raw = row[c.key];
    const val = c.format ? c.format(raw, row) : raw;
    return `<td${c.numeric ? ' class="num"' : ""}>${escapeHtml(val ?? "")}</td>`;
  }).join("")}</tr>`).join("")}</tbody>`;
  return `<table>${thead}${tbody}</table>`;
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
